import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Save, 
  RotateCcw, 
  FileText, 
  ChevronDown, 
  ChevronRight,
  Loader2,
  Check,
  ExternalLink,
  Upload,
  Image,
  Video
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Section display names and icons
const SECTION_CONFIG = {
  seo: { name: "SEO Settings", description: "Site title and meta description for search engines" },
  chatbot: { name: "Chatbot Settings", description: "Customize your chat assistant name, avatar and colors" },
  chatbot_automation: { name: "Chatbot Automation", description: "Configure proactive messages and triggers" },
  hero: { name: "Hero Section", description: "Main banner at the top of the page" },
  snapshot: { name: "Quick Venue Snapshot", description: "Venue stats displayed below the hero" },
  about: { name: "About Section", description: "Information about KAI venue" },
  calculator: { name: "Calculator Labels", description: "Labels in the budget calculator" },
  breakdown: { name: "Budget Breakdown", description: "Budget breakdown section labels" },
  youtube: { name: "YouTube Section", description: "YouTube videos section" },
  instagram: { name: "Instagram Section", description: "Instagram reels section" },
  charts: { name: "Performance Charts", description: "Charts section titles" },
  founder: { name: "Founder's Note", description: "Personal message from the founder" },
  gallery: { name: "Gallery Section", description: "Gallery description and button" },
  lead_form: { name: "Lead Form", description: "Contact form labels" },
  cta: { name: "Call-to-Action Buttons", description: "Button text labels" },
};

// Fields that support file upload
const UPLOAD_FIELDS = {
  hero_logo_url: { type: 'image', accept: 'image/jpeg,image/png,image/webp,image/gif' },
  hero_image_url: { type: 'image', accept: 'image/jpeg,image/png,image/webp,image/gif' },
  hero_video_url: { type: 'video', accept: 'video/mp4,video/webm,video/quicktime' },
  hero_image_url_mobile: { type: 'image', accept: 'image/jpeg,image/png,image/webp,image/gif' },
  hero_video_url_mobile: { type: 'video', accept: 'video/mp4,video/webm,video/quicktime' },
  chatbot_avatar_url: { type: 'image', accept: 'image/jpeg,image/png,image/webp,image/gif' }
};

const AdminContentManager = () => {
  const [content, setContent] = useState({});
  const [sections, setSections] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [changedItems, setChangedItems] = useState(new Set());
  const [uploading, setUploading] = useState({});
  const fileInputRefs = useRef({});

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      // Add cache-busting timestamp to prevent browser caching
      const response = await axios.get(`${API}/site-content/admin?_t=${Date.now()}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      console.log("Fetched sections:", Object.keys(response.data.sections || {}));
      console.log("Hero section items:", response.data.sections?.hero?.length || 0);
      
      setSections(response.data.sections || {});
      
      // Create a flat content object for easy editing
      const contentMap = {};
      (response.data.items || []).forEach(item => {
        contentMap[item.key] = item;
      });
      setContent(contentMap);
      
      // Expand hero section by default (where banner media options are)
      const sectionKeys = Object.keys(response.data.sections || {});
      if (sectionKeys.length > 0) {
        // Always expand hero section if it exists
        const expandSections = { hero: true };
        if (!sectionKeys.includes('hero') && sectionKeys.length > 0) {
          expandSections[sectionKeys[0]] = true;
        }
        setExpandedSections(expandSections);
      }
    } catch (error) {
      console.error("Failed to fetch content:", error);
      toast.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  };

  const handleContentChange = (key, value) => {
    setContent(prev => ({
      ...prev,
      [key]: { ...prev[key], value }
    }));
    setChangedItems(prev => new Set([...prev, key]));
  };

  const handleFileUpload = async (key, file) => {
    const uploadConfig = UPLOAD_FIELDS[key];
    if (!uploadConfig) return;

    setUploading(prev => ({ ...prev, [key]: true }));
    
    try {
      const token = localStorage.getItem("admin_token");
      const formData = new FormData();
      formData.append('file', file);

      const endpoint = uploadConfig.type === 'video' ? '/upload/video' : '/upload/image';
      const response = await axios.post(`${API}${endpoint}`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        // Update the content with the new URL
        const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
        handleContentChange(key, fullUrl);
        toast.success(`${uploadConfig.type === 'video' ? 'Video' : 'Image'} uploaded successfully!`);
      }
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("admin_token");
      const itemsToUpdate = Array.from(changedItems).map(key => ({
        key,
        value: content[key].value
      }));
      
      await axios.post(`${API}/site-content/bulk-update`, { items: itemsToUpdate }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Saved ${itemsToUpdate.length} item(s) successfully!`);
      setChangedItems(new Set());
    } catch (error) {
      console.error("Failed to save content:", error);
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefaults = async () => {
    if (!window.confirm("Are you sure you want to reset all content to defaults? This cannot be undone.")) {
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/site-content/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Content reset to defaults!");
      setChangedItems(new Set());
      fetchContent();
    } catch (error) {
      console.error("Failed to reset content:", error);
      toast.error("Failed to reset content");
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#B08968]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="w-6 h-6 text-[#B08968]" />
              Content Manager
            </h1>
            <p className="text-gray-600 mt-1">
              Edit all text content on your website
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleResetToDefaults}
              disabled={saving}
              className="gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Reset to Defaults
            </Button>
            
            <Button
              onClick={handleSaveAll}
              disabled={saving || changedItems.size === 0}
              className="gap-2 bg-[#B08968] hover:bg-[#9A7558]"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Changes
              {changedItems.size > 0 && (
                <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
                  {changedItems.size}
                </span>
              )}
            </Button>
          </div>
        </div>

        {/* Preview Link */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-blue-700">
            <ExternalLink className="w-4 h-4" />
            <span className="text-sm">Preview your changes on the live site</span>
          </div>
          <a
            href="/weddings"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            Open Website →
          </a>
        </div>

        {/* Content Sections */}
        <div className="space-y-4">
          {Object.entries(SECTION_CONFIG).map(([sectionKey, sectionInfo]) => {
            const sectionItems = sections[sectionKey] || [];
            if (sectionItems.length === 0) return null;
            
            const isExpanded = expandedSections[sectionKey];
            const hasChanges = sectionItems.some(item => changedItems.has(item.key));
            
            return (
              <div 
                key={sectionKey} 
                className="border rounded-lg overflow-hidden bg-white shadow-sm"
              >
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(sectionKey)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                    <div className="text-left">
                      <h3 className="font-medium text-gray-900 flex items-center gap-2">
                        {sectionInfo.name}
                        {hasChanges && (
                          <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">
                            Modified
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-gray-500">{sectionInfo.description}</p>
                    </div>
                  </div>
                  <span className="text-sm text-gray-400">
                    {sectionItems.length} item{sectionItems.length !== 1 ? 's' : ''}
                  </span>
                </button>
                
                {/* Section Content */}
                {isExpanded && (
                  <div className="border-t bg-gray-50 p-4">
                    <div className="space-y-4">
                      {sectionItems.map(item => {
                        const currentContent = content[item.key] || item;
                        const isChanged = changedItems.has(item.key);
                        const uploadConfig = UPLOAD_FIELDS[item.key];
                        const isUploading = uploading[item.key];
                        
                        return (
                          <div 
                            key={item.key} 
                            className={`bg-white rounded-lg p-4 border ${isChanged ? 'border-amber-300 ring-1 ring-amber-200' : 'border-gray-200'}`}
                          >
                            <Label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
                              {uploadConfig?.type === 'image' && <Image className="w-4 h-4 text-blue-500" />}
                              {uploadConfig?.type === 'video' && <Video className="w-4 h-4 text-purple-500" />}
                              {item.label}
                              {isChanged && (
                                <span className="text-amber-600 text-xs">(unsaved)</span>
                              )}
                            </Label>
                            
                            {item.content_type === "textarea" ? (
                              <Textarea
                                value={currentContent.value}
                                onChange={(e) => handleContentChange(item.key, e.target.value)}
                                rows={3}
                                className="w-full resize-none"
                                placeholder={`Enter ${item.label.toLowerCase()}...`}
                              />
                            ) : (
                              <div className="space-y-2">
                                <Input
                                  value={currentContent.value}
                                  onChange={(e) => handleContentChange(item.key, e.target.value)}
                                  className="w-full"
                                  placeholder={`Enter ${item.label.toLowerCase()}...`}
                                />
                                
                                {/* File Upload for image/video fields */}
                                {uploadConfig && (
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="file"
                                      ref={el => fileInputRefs.current[item.key] = el}
                                      accept={uploadConfig.accept}
                                      className="hidden"
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) handleFileUpload(item.key, file);
                                      }}
                                    />
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      disabled={isUploading}
                                      onClick={() => fileInputRefs.current[item.key]?.click()}
                                      className="gap-2"
                                    >
                                      {isUploading ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <Upload className="w-4 h-4" />
                                      )}
                                      {isUploading ? 'Uploading...' : `Upload ${uploadConfig.type === 'video' ? 'Video' : 'Image'}`}
                                    </Button>
                                    
                                    {/* Preview for uploaded content */}
                                    {currentContent.value && (
                                      <a 
                                        href={currentContent.value} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                                      >
                                        <ExternalLink className="w-3 h-3" />
                                        Preview
                                      </a>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            <p className="text-xs text-gray-400 mt-1">
                              Key: <code className="bg-gray-100 px-1 rounded">{item.key}</code>
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Floating Save Button (appears when changes exist) */}
        {changedItems.size > 0 && (
          <div className="fixed bottom-6 right-6 z-50">
            <Button
              onClick={handleSaveAll}
              disabled={saving}
              className="gap-2 bg-[#B08968] hover:bg-[#9A7558] shadow-lg px-6 py-3 h-auto"
              size="lg"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Check className="w-5 h-5" />
              )}
              Save {changedItems.size} Change{changedItems.size !== 1 ? 's' : ''}
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminContentManager;
